import type { MetaFunction } from "@remix-run/node";

export const meta: MetaFunction = () => [
  { title: "Privacy Policy — Sandcastle Admin Search" },
  {
    name: "description",
    content:
      "Privacy policy for the Sandcastle Admin Search Shopify app, published by Sandcastle Interactive LLC.",
  },
];

const LAST_UPDATED = "May 19, 2026";
const COMPANY = "Sandcastle Interactive LLC";
const CONTACT_EMAIL = "support@sandcastle-web.com";
const APP_NAME = "Sandcastle Admin Search";

export default function Privacy() {
  return (
    <main style={pageStyle}>
      <article style={articleStyle}>
        <header>
          <h1 style={h1Style}>Privacy Policy</h1>
          <p style={metaStyle}>
            Last updated: <strong>{LAST_UPDATED}</strong>
          </p>
        </header>

        <section>
          <h2 style={h2Style}>1. Who we are</h2>
          <p>
            {APP_NAME} (&ldquo;the app&rdquo;) is built and operated by{" "}
            {COMPANY} (&ldquo;we&rdquo;, &ldquo;us&rdquo;,
            &ldquo;our&rdquo;). This policy explains what data the app
            collects from Shopify merchants who install it, how we use that
            data, and the choices you have. If you have questions, email{" "}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
          </p>
        </section>

        <section>
          <h2 style={h2Style}>2. Data we collect</h2>
          <p>
            When you install {APP_NAME} on your Shopify store, the app
            requests OAuth scopes that allow it to read and write product
            data. We collect and store only what is necessary to provide the
            app&rsquo;s functionality:
          </p>
          <ul>
            <li>
              <strong>Shopify session tokens.</strong> When you install the
              app, Shopify provides an offline access token tied to your
              shop. We store this so the app can call Shopify&rsquo;s API on
              your behalf during normal operation.
            </li>
            <li>
              <strong>Per-shop preferences.</strong> Settings you configure
              inside the app &mdash; search field defaults, visible columns,
              page size, and named saved views (filter combinations) &mdash;
              are stored in our database keyed to your shop domain.
            </li>
            <li>
              <strong>Operational logs and error reports.</strong> When the
              app processes a request, we log structured operational events
              (such as &ldquo;a full-catalog scan ran for shop X taking N
              milliseconds&rdquo;) and any unhandled errors. Error reports
              include your shop domain so we can investigate per-merchant
              issues, but do not include customer-identifying data.
            </li>
          </ul>
          <p>
            <strong>Product, collection, and metafield data</strong> from
            your store is read live from Shopify&rsquo;s API on each request
            and is <em>not</em> stored on our servers.
          </p>
        </section>

        <section>
          <h2 style={h2Style}>3. Data we do not collect</h2>
          <p>
            {APP_NAME} does <strong>not</strong> request access to customer
            data, orders, payment information, or storefront analytics. The
            app&rsquo;s OAuth scopes are limited to product administration
            and reading sales-channel publication state. We do not collect
            customer email addresses, customer names, or any
            personally-identifiable information about your shoppers.
          </p>
        </section>

        <section>
          <h2 style={h2Style}>4. How we use the data</h2>
          <p>The data we collect is used solely to:</p>
          <ul>
            <li>Authenticate API calls to your Shopify store</li>
            <li>Render your customised app experience (filters, columns, saved views)</li>
            <li>Diagnose and fix bugs that affect your store</li>
            <li>Monitor service health and performance</li>
          </ul>
          <p>
            We do not sell, rent, or trade any of the data we collect. We do
            not use the data for advertising or marketing.
          </p>
        </section>

        <section>
          <h2 style={h2Style}>5. Subprocessors</h2>
          <p>
            We rely on the following third-party services to operate the
            app. Each is a recognised data processor with their own privacy
            commitments:
          </p>
          <ul>
            <li>
              <strong>Shopify, Inc.</strong> &mdash; the platform on which
              the app runs and the source of all product data.
            </li>
            <li>
              <strong>Fly.io</strong> &mdash; hosts the app server, located
              in the United States.
            </li>
            <li>
              <strong>Neon</strong> &mdash; hosts the Postgres database
              storing sessions and per-shop preferences.
            </li>
            <li>
              <strong>Sentry</strong> &mdash; receives error reports and
              performance telemetry to help us diagnose issues.
            </li>
          </ul>
        </section>

        <section>
          <h2 style={h2Style}>6. Where we process your data</h2>
          <p>
            {COMPANY} is established in the United States. All data we
            collect &mdash; session tokens, per-shop preferences, and
            operational logs &mdash; is stored and processed in the
            United States by our hosting and database subprocessors
            (Fly.io and Neon, both with primary US-region infrastructure).
          </p>
          <p>
            We are not established in the European Economic Area (EEA),
            United Kingdom, or Switzerland. If you install the app from a
            shop located in those regions, data about your shop is
            transferred to and processed in the United States. We rely on
            Shopify&rsquo;s own data-protection commitments to merchants
            as the basis for this transfer; if your jurisdiction requires
            an additional data-processing agreement directly with us,
            please contact{" "}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
          </p>
        </section>

        <section>
          <h2 style={h2Style}>7. Data retention and deletion</h2>
          <p>
            We retain your shop&rsquo;s session and preferences only as long
            as the app is installed on your store. When you uninstall the
            app:
          </p>
          <ul>
            <li>
              Shopify immediately notifies us via the{" "}
              <code>app/uninstalled</code> webhook, and we delete your
              session record.
            </li>
            <li>
              48 hours later, Shopify sends the GDPR{" "}
              <code>shop/redact</code> webhook. Upon receipt we delete all
              remaining data associated with your shop, including saved
              preferences and saved views.
            </li>
          </ul>
          <p>
            Operational logs are retained for up to 30 days for diagnostic
            purposes, after which they are purged automatically. Error
            reports in Sentry are retained according to Sentry&rsquo;s
            current retention policy for our subscription tier.
          </p>
        </section>

        <section>
          <h2 style={h2Style}>8. Customer GDPR webhooks</h2>
          <p>
            Shopify requires every public app to handle GDPR data-subject
            requests on behalf of merchants&rsquo; customers. Because this
            app does not store any customer-identifying data, our
            implementation of the <code>customers/data_request</code> and{" "}
            <code>customers/redact</code> webhooks logs the request and
            returns a successful response &mdash; there is no customer data
            for us to export or delete.
          </p>
        </section>

        <section>
          <h2 style={h2Style}>9. Your rights</h2>
          <p>
            Depending on your jurisdiction, you may have rights regarding
            the data we hold about your shop, including the right to access,
            correct, or delete it. To exercise any of these rights, email{" "}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> from an
            address associated with your Shopify Partner or store admin
            account. We will respond within 30 days.
          </p>
          <p>
            You can also delete all data we hold about your shop at any
            time by uninstalling the app from your Shopify admin.
          </p>
        </section>

        <section>
          <h2 style={h2Style}>10. Children&rsquo;s privacy</h2>
          <p>
            {APP_NAME} is a business-to-business product designed for
            merchants operating Shopify stores. It is not directed at
            children under 13, and we do not knowingly collect data from
            them.
          </p>
        </section>

        <section>
          <h2 style={h2Style}>11. Changes to this policy</h2>
          <p>
            We may update this policy from time to time. The
            &ldquo;Last updated&rdquo; date at the top reflects the most
            recent change. Material changes will be communicated via the
            app&rsquo;s in-product notifications or by email to the
            installation contact registered with your Shopify Partner
            account.
          </p>
        </section>

        <section>
          <h2 style={h2Style}>12. Contact</h2>
          <p>
            Questions or concerns about this policy? Email{" "}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
          </p>
        </section>
      </article>
    </main>
  );
}

const pageStyle: React.CSSProperties = {
  minHeight: "100vh",
  backgroundColor: "#f6f6f7",
  padding: "48px 24px",
  fontFamily:
    "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  color: "#202223",
  lineHeight: 1.6,
};

const articleStyle: React.CSSProperties = {
  maxWidth: 760,
  margin: "0 auto",
  backgroundColor: "#ffffff",
  borderRadius: 12,
  padding: "48px 56px",
  boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
};

const h1Style: React.CSSProperties = {
  fontSize: 32,
  fontWeight: 600,
  margin: "0 0 8px",
};

const h2Style: React.CSSProperties = {
  fontSize: 20,
  fontWeight: 600,
  margin: "32px 0 12px",
};

const metaStyle: React.CSSProperties = {
  color: "#6d7175",
  fontSize: 14,
  margin: "0 0 24px",
};
